/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Warnings sub-tab — per-player warning lookup + add + clear.
 *
 * Workflow: type a player ID, hit Look up. We display the existing
 * warnings for that player and let admins add a new one or clear all.
 */
import { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { AlertTriangle, Plus, Trash2, Search } from "lucide-react";

export default function WarningsTab({ toast }) {
  const [pid, setPid] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [reason, setReason] = useState("");
  const [server, setServer] = useState("");
  const [servers, setServers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/moderation/servers").then(({ data }) => setServers(data.servers || []));
  }, []);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!pid.trim()) return;
    try {
      const { data } = await api.get(`/admin/moderation/warnings/${encodeURIComponent(pid.trim())}`);
      setWarnings(data.warnings || []);
      setLoaded(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!pid || !reason) { toast.error("Player ID and reason are required"); return; }
    try {
      const { data } = await api.post("/admin/moderation/warnings", { player_id: pid.trim(), reason, ...(server ? { server } : {}) });
      toast.success(`Warning added. Count is now ${data.warning_count}.`);
      setReason("");
      lookup();
    } catch (e) { toast.error(apiError(e)); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Clear ALL warnings for ${pid}?`)) return;
    try {
      const { data } = await api.delete(`/admin/moderation/warnings/${encodeURIComponent(pid)}`);
      toast.success(`Cleared ${data.cleared} warning${data.cleared === 1 ? "" : "s"}`);
      setWarnings([]);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={lookup} className="glass-card p-4 flex gap-2" data-testid="warn-lookup-form">
        <input value={pid} onChange={(e) => setPid(e.target.value)} placeholder="Player ID" className="input flex-1" data-testid="warn-player-id" />
        <button className="btn"><Search size={14} /> Look up</button>
      </form>

      {loaded && (
        <>
          <form onSubmit={add} className="glass-card p-4 space-y-3" data-testid="warn-add-form">
            <h3 className="label flex items-center gap-2"><Plus size={14} /> Add Warning</h3>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="input" data-testid="warn-reason" />
            <select value={server} onChange={(e) => setServer(e.target.value)} className="input" data-testid="warn-server">
              <option value="">No specific server</option>
              {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <button className="btn btn-amber"><AlertTriangle size={14} /> Add Warning</button>
          </form>

          <div className="glass-card p-4" data-testid="warn-list">
            <div className="flex items-center justify-between mb-3">
              <h3 className="label">Warnings for <span className="font-mono text-[var(--bone)]">{pid}</span></h3>
              {warnings.length > 0 && (
                <button onClick={clearAll} className="btn btn-ghost text-rose-300" data-testid="warn-clear-all"><Trash2 size={14} /> Clear All</button>
              )}
            </div>
            {warnings.length === 0 ? (
              <p className="text-[var(--muted)] text-sm">No warnings on file.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {warnings.map((w) => (
                  <li key={w.id} className="py-2 text-sm flex justify-between gap-2" data-testid={`warn-row-${w.id}`}>
                    <span className="text-[var(--bone)]">{w.reason}</span>
                    <span className="text-[var(--muted)] text-xs font-mono">{w.warned_at}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
